import React, { useEffect, useState } from 'react';
import { Table, Button, Tag, Typography, Space, Input, Statistic, Row, Col, Card, App, Tabs, Select } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import api from '@/api/client';
import type { PaginatedResponse } from '@/types';
import dayjs from 'dayjs';

const { Title } = Typography;

export default function CreditsPage() {
  const [data, setData] = useState<PaginatedResponse<any>>({ items: [], total: 0, page: 1, pageSize: 20 });
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [stats, setStats] = useState<{ 
    totalRevenue?: number; 
    totalOrders?: number; 
    todayRevenue?: number; 
    todayOrders?: number;
    totalConsumed?: number;
    totalRecharged?: number;
  }>({});
  const { message } = App.useApp();

  const loadTransactions = async (page = 1) => {
    setLoading(true);
    try {
      const params: any = { page, pageSize: 20 };
      if (userId) params.userId = parseInt(userId);
      if (typeFilter) params.type = typeFilter;
      const [txRes, statsRes] = await Promise.all([
        api.get('/credits/transactions', { params }),
        api.get('/revenue/stats'),
      ]);
      setData(txRes.data);
      setStats(statsRes.data);
    } catch {
      message.error('加载积分数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTransactions(); }, [typeFilter]);

  const typeMap: Record<string, { color: string; label: string }> = {
    recharge: { color: 'green', label: '充值' },
    consume: { color: 'red', label: '消耗' },
    manual: { color: 'blue', label: '管理员调整' },
    admin_adjust: { color: 'blue', label: '管理员调整' },
    task_daily_login: { color: 'purple', label: '任务奖励' },
    task_reward: { color: 'purple', label: '任务奖励' },
    invite_reward: { color: 'orange', label: '邀请奖励' },
    feedback_reward: { color: 'gold', label: '反馈奖励' },
    freeze: { color: 'orange', label: '预扣' },
    cancel_freeze: { color: 'cyan', label: '取消预扣' },
    refund: { color: 'cyan', label: '退款' },
    gift: { color: 'pink', label: '赠送' },
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '用户ID', dataIndex: 'user_id', width: 80 },
    { title: '用户名', dataIndex: 'username', width: 100 },
    { 
      title: '变动', 
      dataIndex: 'amount', 
      width: 80, 
      render: (v: number) => (
        <span style={{ 
          color: v > 0 ? '#52c41a' : '#ff4d4f',
          fontWeight: 500
        }}>
          {v > 0 ? `+${v}` : v}
        </span>
      ) 
    },
    {
      title: '类型', 
      dataIndex: 'type', 
      width: 100,
      render: (t: string) => <Tag color={typeMap[t]?.color}>{typeMap[t]?.label || t}</Tag>,
    },
    { title: '说明', dataIndex: 'description', ellipsis: true },
    { 
      title: '余额', 
      dataIndex: 'balance_after', 
      width: 80,
      render: (v: number) => v ?? '-'
    },
    {
      title: '时间', 
      dataIndex: 'created_at', 
      width: 150,
      render: (v: string) => v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
  ];

  const typeOptions = [
    { label: '全部类型', value: '' },
    { label: '充值', value: 'recharge' },
    { label: '消耗', value: 'consume' },
    { label: '管理员调整', value: 'manual' },
    { label: '任务奖励', value: 'task_daily_login' },
    { label: '邀请奖励', value: 'invite_reward' },
    { label: '反馈奖励', value: 'feedback_reward' },
    { label: '预扣', value: 'freeze' },
    { label: '取消预扣', value: 'cancel_freeze' },
    { label: '退款', value: 'refund' },
    { label: '赠送', value: 'gift' },
  ];

  return (
    <div>
      <Title level={4} style={{ marginBottom: 24 }}>积分管理</Title>
      
      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic 
              title="总营收" 
              value={stats.totalRevenue || 0} 
              prefix="¥" 
              precision={2} 
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic 
              title="总充值积分" 
              value={stats.totalRecharged || 0} 
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic 
              title="总消耗积分" 
              value={stats.totalConsumed || 0} 
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic 
              title="今日营收" 
              value={stats.todayRevenue || 0} 
              prefix="¥" 
              precision={2} 
            />
          </Card>
        </Col>
      </Row>

      {/* 筛选和刷新 */}
      <Space style={{ marginBottom: 16 }}>
        <Input.Search 
          placeholder="按用户ID搜索" 
          value={userId} 
          onChange={(e) => setUserId(e.target.value)} 
          onSearch={() => loadTransactions()} 
          style={{ width: 200 }} 
        />
        <Select
          placeholder="按类型筛选"
          value={typeFilter || undefined}
          onChange={setTypeFilter}
          style={{ width: 150 }}
          options={typeOptions}
          allowClear
        />
        <Button icon={<ReloadOutlined />} onClick={() => loadTransactions()}>刷新</Button>
      </Space>

      {/* 积分变动记录表 */}
      <Table
        columns={columns} 
        dataSource={data.items} 
        rowKey="id" 
        loading={loading}
        pagination={{ 
          current: data.page, 
          pageSize: data.pageSize, 
          total: data.total, 
          onChange: loadTransactions, 
          showSizeChanger: false 
        }}
      />
    </div>
  );
}
